import Box from "@/common/components/Box";
import { ImageCarousel } from "@/common/components/ImageCarousel";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { ChevronDown, ChevronUp, WrenchIcon } from "lucide-react";
import ResumeItem from "@/common/components/ResumeItem";
import { formatCurrency, formatUnit } from "@/common/utils/formatters";
import { DiagnosticPieceData } from "@/features/maintenance/features/workflows/types/forwardings/directiveEntries/diagnosticEntry/diagnosticEntry.type";
import DeleteDiagnosticObjectivePiece from "./DeleteDiagniosticObjectiveActivity";
import UpdateDiagnosticObjectivePiece from "./UpdateDiagnosticObjectiveActivity";
import Button from "@/common/components/Button";
import { useState } from "react";
import MaintenancePieceAdditionalInformationAccordion from "@/features/maintenance/features/workflows/features/diagnosticEntry/screens/Update/components/MaintenancePieceAdditionalInformationAccordion";

const DiagnosticObjectivePieceItem = ({
  diagnosticPiece,
  workflowId,
}: {
  diagnosticPiece: DiagnosticPieceData;
  workflowId: string | undefined;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const files =
    diagnosticPiece.piece?.images?.map((image) => ({
      id: image?.url,
      file: image?.url,
    })) || [];

  return (
    <Box className="p-4 gap-2">
      {isCollapsed ? (
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <ImageCarousel
              files={files}
              Placeholder={
                <ImageWithPlaceholder
                  className="size-10 rounded-md"
                  iconClassName="size-5"
                  placeholderIcon={WrenchIcon}
                />
              }
              className="size-10 shrink-0 bg-[#fff]"
              imageClassName="object-contain"
              showExpandButton
              showRemoveButton={false}
            />

            <p className="text-sm font-bold truncate">
              {diagnosticPiece?.piece?.display_name || "--"}
            </p>

            <div className="flex items-center gap-2 text-xs text-gray-400 shrink-0">
              <span>
                {formatCurrency(Number(diagnosticPiece?.unit_price) || 0)}
              </span>
              <span>×</span>
              <span>{formatUnit(diagnosticPiece.quantity, "unit")}</span>
              <span>=</span>
              <span className="text-valid font-semibold">
                {formatCurrency(Number(diagnosticPiece?.total_value) || 0)}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <DeleteDiagnosticObjectivePiece diagnosticPiece={diagnosticPiece} />

            <UpdateDiagnosticObjectivePiece diagnosticPiece={diagnosticPiece} />

            <Button variant="link-white" onClick={() => setIsCollapsed(false)}>
              <ChevronDown strokeWidth={1} className="text-white" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex gap-4">
              <ImageCarousel
                files={files}
                Placeholder={
                  <ImageWithPlaceholder
                    className="size-28 rounded-md"
                    iconClassName="size-10"
                    placeholderIcon={WrenchIcon}
                  />
                }
                className="size-28 bg-[#fff]"
                imageClassName="object-contain"
                showExpandButton
                showNavigationButtons={false}
                showRemoveButton={false}
                maxDots={5}
              />

              <div className="flex flex-col gap-2">
                <p className="text-base font-bold">
                  {diagnosticPiece?.piece?.display_name || "--"}
                </p>

                <div className="flex items-center flex-wrap gap-3">
                  <ResumeItem
                    className="text-sm"
                    label="Valor unt."
                    value={formatCurrency(
                      Number(diagnosticPiece?.unit_price) || 0,
                    )}
                  />

                  <p className="text-sm">x</p>

                  <ResumeItem
                    className="text-sm"
                    label="Qntd"
                    value={formatUnit(diagnosticPiece.quantity, "unit")}
                  />

                  <p className="text-sm">=</p>

                  <ResumeItem
                    className="text-sm"
                    label="Valor total"
                    value={formatCurrency(
                      Number(diagnosticPiece?.total_value) || 0,
                    )}
                  />

                  <ResumeItem
                    className="text-sm"
                    label="Garantia"
                    value={`${diagnosticPiece.warranty_in_days} Dias`}
                  />

                  <ResumeItem
                    className="text-sm"
                    label="Código da peça"
                    value={diagnosticPiece?.piece?.number || "--"}
                  />

                  <ResumeItem
                    className="text-sm"
                    label="Fabricante"
                    value={diagnosticPiece?.piece?.brand?.display_name || "--"}
                  />
                </div>

                <ResumeItem
                  className="text-sm"
                  label="Descrição"
                  value={diagnosticPiece.description}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-4">
            <Button variant="link-white" onClick={() => setIsCollapsed(true)}>
              <ChevronUp strokeWidth={1} className="text-white" />
            </Button>

            <div className="ml-auto flex gap-3">
              <DeleteDiagnosticObjectivePiece
                diagnosticPiece={diagnosticPiece}
              />

              <UpdateDiagnosticObjectivePiece
                diagnosticPiece={diagnosticPiece}
              />
            </div>
          </div>
        </div>
      )}

      <MaintenancePieceAdditionalInformationAccordion
        workflowId={workflowId}
        pieceId={diagnosticPiece.piece?.id}
      />
    </Box>
  );
};

export default DiagnosticObjectivePieceItem;
